/**
 * Token Manager — single source of truth for the access token + refresh logic.
 *
 * Part of the auth hardening cutover (see .cursor/plans/auth_security.plan.md,
 * Phase 4). Designed to work in ALL backend AUTH_MODE values from one bundle:
 *
 *   - legacy : refresh token lives in localStorage, sent in the request body.
 *   - dual   : refresh token in localStorage (body) AND in an httpOnly cookie;
 *              cookie is preferred by the backend, body still accepted.
 *   - strict : refresh only via httpOnly cookie (later steps remove localStorage).
 *
 * STEP 4.1 scope: introduce a centralized accessor + single-flight refresh
 * WITHOUT changing observable behavior. The access token is still mirrored to
 * localStorage so existing readers keep working; a later step removes the mirror
 * and makes the in-memory copy the only one.
 */
import axios from 'axios'
import { ref } from 'vue'

import { isNative } from '@/native/platform'
import { secureSet, secureGetCached, secureRemove, secureGet, preloadSecure } from '@/native/secureStorage'

// Key names duplicated here to avoid a circular import with native/biometric.js.
const BIOMETRIC_PHONE_KEY = 'token_quick_login_phone'
const BIOMETRIC_REFRESH_KEY = 'biometric_refresh_token'
const PASSKEY_PHONE_KEY = 'passkey_phone'
import { isTechPreview, previewToken, notifyParent } from '@/preview/techPreview'

// Native shells talk cross-site to the API, so the refresh endpoint must be an
// absolute URL (a relative path resolves against capacitor://localhost). On web
// the var is unset → same-origin '/api/v1/...' as before.
const _API_BASE = (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '')
const REFRESH_URL = _API_BASE ? `${_API_BASE}/api/v1/auth/refresh/` : '/api/v1/auth/refresh/'

// Key used for the refresh token in secure storage (native).
const REFRESH_TOKEN_KEY = 'refresh_token'

// In-memory access token — the single source of truth (Phase 4.6). Held in a Vue
// ref so reactive consumers (e.g. the Pinia `accessToken` getter that proxies
// here) recompute when the token changes after a login / silent refresh / rotation.
const _accessTokenRef = ref(null)
// Single-flight guard: concurrent 401s share ONE refresh request. Critical once
// access tokens are short-lived (15 min) so a burst of parallel requests does
// not trigger a refresh stampede (and, under strict rotation, a self-inflicted
// reuse-detection logout).
let _refreshPromise = null
// Whether the LAST refresh established an httpOnly auth cookie (dual/strict).
// Used to gate the one-time purge of legacy localStorage tokens — we must NOT
// purge in legacy mode (no cookie ⇒ the client would lose its only credential).
let _lastCookieAuth = false
// Boot gate: on page load the in-memory access token is empty and the short-lived
// (15-min) access cookie may have expired, so the very first API requests would
// 401 until the silent boot refresh repopulates the token. The apiClient request
// interceptor awaits this gate so those first requests carry a fresh token and do
// not flash transient 401s. Null = no gate (anonymous/God Mode/fresh login).
let _bootGate = null

// Proactive refresh: the access token is short-lived (15 min in dual/strict). If we
// only refresh REACTIVELY (on a 401) an idle operator's token + access cookie expire
// while they wait for a call, and the burst of requests/WS reconnects on call pickup
// races the refresh — which intermittently 401s and hard-logs-them-out. Refreshing a
// bit BEFORE expiry keeps the in-memory token + httpOnly cookie always fresh, so the
// token never goes stale and the reactive-401 storm never happens.
let _proactiveTimer = null

function _clearProactiveTimer() {
  try { if (_proactiveTimer) { clearTimeout(_proactiveTimer); _proactiveTimer = null } } catch (e) { /* ignore */ }
}

/**
 * (Re)arm the proactive refresh based on the server's expires_in (seconds). Called
 * after login and after every successful refresh. God Mode is skipped (its token is
 * a short-lived sessionStorage token with no refresh cookie). Self-reschedules via
 * the refresh response, so one call keeps the session alive indefinitely.
 */
export function scheduleProactiveRefresh(expiresInSec) {
  _clearProactiveTimer()
  if (_isGodMode() || isTechPreview()) return
  const sec = Number(expiresInSec)
  if (!sec || !isFinite(sec) || sec <= 0) return
  // Refresh 90s before expiry, but never less than 5s from now (guards tiny TTLs).
  let delay = sec * 1000 - 90 * 1000
  if (delay < 5 * 1000) delay = Math.max(5 * 1000, sec * 1000 - 10 * 1000)
  _proactiveTimer = setTimeout(async () => {
    _proactiveTimer = null
    try {
      await refreshAccessToken(axios)
    } catch (e) {
      // Offline/transient — do NOT log out here (the reactive 401 path owns that).
      // Retry soon so we don't drift into a stale token.
      _clearProactiveTimer()
      _proactiveTimer = setTimeout(() => { refreshAccessToken(axios).catch(() => {}) }, 30 * 1000)
    }
  }, delay)
  try { console.log('[tokenManager] proactive refresh in', Math.round(delay / 1000), 's') } catch (e) {}
}

// Arm the boot gate with the silent-refresh promise. main.js sets this BEFORE the
// app mounts. A safety timeout in main.js guarantees the gate always settles.
export function beginBootGate(promise) {
  _bootGate = promise || null
}

// Awaited by the request interceptor. Returns the in-flight boot refresh (if any),
// otherwise a resolved promise (instant). Never rejects — boot refresh failures
// must not block requests (they fall through to the normal 401→refresh path).
export function awaitBootGate() {
  return _bootGate || Promise.resolve()
}

function _isGodMode() {
  return !!sessionStorage.getItem('god_mode')
}

export function getAccessToken() {
  // Tech App Preview iframe: the short-lived preview token is the ONLY
  // credential. It never touches storage and is never refreshed.
  if (isTechPreview()) return previewToken()
  if (_accessTokenRef.value) return _accessTokenRef.value
  // God Mode tabs carry their own short-lived token in sessionStorage (no cookie,
  // no refresh) and reload-survive via it.
  if (_isGodMode()) return sessionStorage.getItem('access_token')
  // Legacy/transition fallback: a token may still be mirrored in localStorage on
  // legacy AUTH_MODE domains. Cookie sessions (dual/strict) purge this mirror, so
  // here it returns null and auth rides the httpOnly cookie until the boot
  // silent-refresh repopulates the in-memory token.
  return localStorage.getItem('access_token')
}

export function setAccessToken(token) {
  _accessTokenRef.value = token || null
  if (!token) return
  // God Mode persists to sessionStorage (tab-isolated).
  if (_isGodMode()) {
    try { sessionStorage.setItem('access_token', token) } catch (e) { /* ignore */ }
    return
  }
  // Keep an EXISTING legacy mirror fresh, but NEVER create one for cookie
  // sessions: once purged (dual/strict), the token lives only in memory + the
  // httpOnly cookie and is invisible to XSS.
  try {
    if (localStorage.getItem('access_token') !== null) {
      localStorage.setItem('access_token', token)
    }
  } catch (e) { /* ignore */ }
}

export function clearAccessToken() {
  _accessTokenRef.value = null
  _clearProactiveTimer()
  try { localStorage.removeItem('access_token') } catch (e) { /* ignore */ }
  try { sessionStorage.removeItem('access_token') } catch (e) { /* ignore */ }
}

export function getRefreshToken() {
  // Native: the refresh token lives in secure storage (the httpOnly cookie is
  // SameSite=Strict and never sent cross-site), read it from the in-memory
  // mirror so the body-refresh path works synchronously on a 401.
  if (isNative()) {
    const cached = secureGetCached(REFRESH_TOKEN_KEY)
    if (cached) return cached
  }
  // Web: legacy/transition only — a body refresh token may still linger in
  // storage until the one-time purge runs. In dual/strict the httpOnly cookie
  // carries it.
  return sessionStorage.getItem('refresh_token') || localStorage.getItem('refresh_token')
}

/** True if the most recent refresh set an httpOnly auth cookie (dual/strict). */
export function lastRefreshUsedCookie() {
  return _lastCookieAuth
}

/**
 * One-time migration cleanup: drop JS-readable tokens from localStorage. Safe to
 * call only AFTER a refresh that set the httpOnly cookie (lastRefreshUsedCookie).
 * God Mode sessionStorage tokens are left untouched (they have no cookie).
 */
export function purgeLegacyStoredTokens() {
  try { localStorage.removeItem('access_token') } catch (e) { /* ignore */ }
  try { localStorage.removeItem('refresh_token') } catch (e) { /* ignore */ }
}

/**
 * Persist the refresh token. Native → encrypted secure storage (Keystore/Keychain);
 * web behaviour is unchanged (the auth store keeps writing to local/sessionStorage
 * for legacy/dual mode).
 * @param {string} token
 */
export function setRefreshToken(token) {
  if (!token) return
  if (isNative()) {
    // Do NOT mirror to localStorage on native — keep it only in secure storage.
    secureSet(REFRESH_TOKEN_KEY, token).catch(() => {})
  }
}

/** Await secure persistence (native login / biometric bind). */
export async function persistRefreshToken(token) {
  if (!token) return
  if (isNative()) {
    await secureSet(REFRESH_TOKEN_KEY, token)
  }
}

/**
 * Remove the refresh token from every store (native secure storage + web).
 */
export function clearRefreshToken() {
  if (isNative()) secureRemove(REFRESH_TOKEN_KEY).catch(() => {})
  try { localStorage.removeItem('refresh_token') } catch { /* ignore */ }
  try { sessionStorage.removeItem('refresh_token') } catch { /* ignore */ }
}

/**
 * Warm the in-memory refresh-token mirror from secure storage (native only).
 * Call once at app startup before the first 401 can occur. No-op on web.
 */
export async function loadStoredTokens() {
  if (!isNative()) return
  await preloadSecure([
    REFRESH_TOKEN_KEY,
    BIOMETRIC_PHONE_KEY,
    BIOMETRIC_REFRESH_KEY,
    PASSKEY_PHONE_KEY,
  ])
}

/**
 * Refresh the access token. Single-flight: all concurrent callers await the same
 * in-flight request. Sends the body refresh token when available (legacy/dual)
 * and always sends credentials so the httpOnly cookie is used in dual/strict.
 *
 * @param {import('axios').AxiosStatic} axiosLib raw axios (NOT the apiClient, to
 *        avoid recursive interceptors).
 * @returns {Promise<string>} the new access token.
 */
function _readCookie(name) {
  try {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
    return m ? decodeURIComponent(m[1]) : null
  } catch (e) { return null }
}

async function _doRefresh(axiosLib) {
  const lib = axiosLib || axios
  const body = {}
  const rt = getRefreshToken()
  if (rt) body.refresh_token = rt
  const headers = {}
  // CSRF double-submit: echo the non-httpOnly fsj_csrf cookie as a header. The
  // backend enforces it only in strict cookie-refresh; harmless otherwise.
  const csrf = _readCookie('fsj_csrf')
  if (csrf) headers['X-CSRF-Token'] = csrf
  // REFRESH_URL is absolute on native (capacitor shell talks cross-site to the
  // API) and relative same-origin on web.
  const res = await lib.post(REFRESH_URL, body, {
    withCredentials: true, timeout: 15000, headers,
  })
  const token = res?.data?.access_token
  _lastCookieAuth = !!res?.data?.cookie_auth
  if (token) setAccessToken(token)
  // Keep the session always-fresh: arm the next proactive refresh before this
  // new token expires, so it never goes stale (no reactive-401 storm on call pickup).
  if (token && res?.data?.expires_in) scheduleProactiveRefresh(res.data.expires_in)
  return token
}

export function refreshAccessToken(axiosLib) {
  // Preview iframe: refreshing would ride the ADMIN's httpOnly cookie and
  // silently swap the tech identity for the admin's — forbidden. The parent
  // page owns session renewal (it mints a fresh preview token).
  if (isTechPreview()) {
    notifyParent('tech_preview_unauthorized')
    return Promise.reject(new Error('Preview session expired'))
  }
  if (_refreshPromise) return _refreshPromise

  _refreshPromise = (async () => {
    // CROSS-TAB single-flight via the Web Locks API. Multiple tabs share ONE
    // httpOnly refresh cookie; under strict rotation a concurrent refresh from a
    // second tab would replay the just-rotated parent and trip reuse-detection →
    // mass logout. The exclusive lock serializes refreshes browser-wide, so each
    // tab picks up the LATEST rotated cookie instead of racing. Falls back to a
    // plain call where Web Locks is unavailable (the backend grace-window covers
    // that case). The in-memory _refreshPromise still coalesces within a tab.
    try {
      if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
        return await navigator.locks.request('fsj_token_refresh', { mode: 'exclusive' },
          async () => _doRefresh(axiosLib))
      }
    } catch (e) {
      // An HTTP rejection from _doRefresh (400/401/403...) is a REAL refresh
      // failure — propagate it. Retrying outside the lock would fire a second
      // doomed request and hide the status from the 401-interceptor logic.
      if (e?.response || e?.isAxiosError) throw e
      // Lock acquisition failed for some reason — fall through to a direct refresh.
      console.warn('[tokenManager] Web Locks refresh failed, falling back:', e?.message || e)
    }
    return _doRefresh(axiosLib)
  })()

  // Clear the guard once settled so the NEXT expiry can refresh again. The
  // trailing .catch swallows rejection ON THIS housekeeping chain only — real
  // callers still receive the rejection from the returned promise. Without it a
  // failed boot refresh (401) surfaced as an "Uncaught (in promise)" and was
  // logged to the DB as a spurious error on every load.
  _refreshPromise.finally(() => { _refreshPromise = null }).catch(() => {})
  return _refreshPromise
}
